import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import TabletIcon from '@mui/icons-material/Tablet';
import TvIcon from '@mui/icons-material/Tv';
import {
  Alert,
  Box,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

import {
  ChromeTheme,
  DeviceType,
  GalleryPreview,
  Orientation,
  SiteContextType,
} from '../../types/previewStudio';
import { extractVideoInfo } from '../../util/galleryVideoUtils';
import DeviceFrame from './DeviceFrame';

interface GalleryPresentationCardProps {
  preview: GalleryPreview;
  apiHost: string;
  deviceType: DeviceType;
  siteContext: SiteContextType;
  orientation: Orientation;
  chromeTheme: ChromeTheme;
  showSizeLabel: boolean;
  showNameLabel: boolean;
  zoomScale: number;
  selected: boolean;
  onToggleSelect: (id: number) => void;
  onDeviceTypeChange: (creativeId: number, dt: DeviceType) => void;
  cardRef: (el: HTMLDivElement | null) => void;
}

function buildMraidMock(adWidth: number, adHeight: number): string {
  // Lightweight inline version for srcdoc — same as GalleryPreviewCard
  return `<script>
(function() {
  if (typeof window.mraid !== 'undefined') return;
  var mraidState = 'default';
  var eventListeners = {};
  var defaultPosition = { x: 0, y: 0, width: ${adWidth}, height: ${adHeight} };
  var expandedPosition = { x: 0, y: 0, width: 320, height: 480 };
  function fireEvent(name, data) {
    if (eventListeners[name]) {
      eventListeners[name].forEach(function(fn) { try { fn(data); } catch(e) {} });
    }
  }
  window.mraid = {
    VERSION: '2.0',
    getVersion: function() { return '2.0'; },
    getState: function() { return mraidState; },
    addEventListener: function(e, fn) { if (!eventListeners[e]) eventListeners[e] = []; eventListeners[e].push(fn); },
    removeEventListener: function(e, fn) { if (eventListeners[e]) { var idx = eventListeners[e].indexOf(fn); if (idx > -1) eventListeners[e].splice(idx, 1); } },
    expand: function() { if (mraidState === 'default') { window.parent.postMessage({ type: 'mraid_expand', width: expandedPosition.width, height: expandedPosition.height }, '*'); mraidState = 'expanded'; fireEvent('stateChange', 'expanded'); } },
    close: function() { if (mraidState === 'expanded') { window.parent.postMessage({ type: 'mraid_close' }, '*'); mraidState = 'default'; fireEvent('stateChange', 'default'); } },
    open: function(url) { if (url && url.trim()) window.open(url, '_blank'); },
    getPlacementType: function() { return 'inline'; },
    useCustomClose: function() {},
    setExpandProperties: function(p) { if (p.width) expandedPosition.width = p.width; if (p.height) expandedPosition.height = p.height; },
    getExpandProperties: function() { return { width: expandedPosition.width, height: expandedPosition.height, useCustomClose: false, isModal: true }; },
    getScreenSize: function() { return { width: 375, height: 667 }; },
    getMaxSize: function() { return { width: 375, height: 667 }; },
    getCurrentPosition: function() { return mraidState === 'expanded' ? expandedPosition : defaultPosition; },
    getDefaultPosition: function() { return defaultPosition; },
    supports: function(f) { return ['sms','tel','calendar','storePicture','inlineVideo'].indexOf(f) > -1; },
    isViewable: function() { return true; },
    playVideo: function(url) { if (url) window.open(url, '_blank'); },
    storePicture: function() {},
    createCalendarEvent: function() {}
  };
  window.addEventListener('message', function(e) { if (e.data && e.data.type === 'trigger_mraid_close') { window.mraid.close(); } });
  setTimeout(function() { fireEvent('ready'); fireEvent('viewableChange', true); }, 50);
})();
${'</'}script>`;
}

export default function GalleryPresentationCard({
  preview,
  apiHost,
  deviceType,
  siteContext,
  orientation,
  chromeTheme,
  showSizeLabel,
  showNameLabel,
  zoomScale,
  selected,
  onToggleSelect,
  onDeviceTypeChange,
  cardRef,
}: GalleryPresentationCardProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const celtraTag = (() => {
    if (!preview.adm) return false;
    try { return atob(preview.adm).includes('celtra.com'); } catch { return false; }
  })();

  const videoInfo = preview.is_video ? extractVideoInfo(preview, deviceType) : null;

  const w = videoInfo?.width || preview.width || 300;
  const h = videoInfo?.height || preview.height || 250;

  const [isExpanded, setIsExpanded] = useState(false);
  const [expandDims, setExpandDims] = useState({ width: 320, height: 480 });

  useEffect(() => {
    if (!preview.is_mraid) return undefined;

    function handleMessage(event: MessageEvent) {
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
      const { data } = event;
      if (!data || !data.type) return;

      if (data.type === 'mraid_expand') {
        setExpandDims({ width: Math.min(data.width || 320, 375), height: Math.min(data.height || 480, 667) });
        setIsExpanded(true);
      } else if (data.type === 'mraid_close') {
        setIsExpanded(false);
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [preview.is_mraid]);

  const handleClose = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ type: 'trigger_mraid_close' }, '*');
    }
    setTimeout(() => setIsExpanded(false), 100);
  };

  const srcdocHtml = React.useMemo(() => {
    if (preview.adm) {
      let adm: string;
      try {
        adm = atob(preview.adm);
      } catch {
        return null;
      }
      if (!adm.trim()) return null;

      const mraidScript = preview.is_mraid || celtraTag ? buildMraidMock(w, h) : '';
      const celtraCss = celtraTag ? `div[class^="celtra-ad-v"]{width:${w}px!important;height:${h}px!important;overflow:hidden!important;position:relative!important;}` : '';
      return `<!DOCTYPE html>
<html style="margin:0;padding:0;width:${w}px;height:${h}px;overflow:hidden;">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=${w},initial-scale=1,maximum-scale=1,user-scalable=no">
<style>
*{box-sizing:border-box;margin:0;padding:0;}
html,body{margin:0!important;padding:0!important;width:${w}px!important;height:${h}px!important;overflow:hidden!important;background:transparent;}
body{display:flex!important;align-items:center!important;justify-content:center!important;position:relative;}
${celtraCss}
</style>
${mraidScript}
</head>
<body>${adm}</body>
</html>`;
    }
    return null;
  }, [preview.adm, preview.is_mraid, celtraTag, w, h]);

  const srcUrl = !srcdocHtml && preview.preview_url ? `${apiHost}${preview.preview_url}` : null;

  if (preview.status === 'error') {
    return (
      <Alert severity="error">{preview.error || 'Failed to generate preview'}</Alert>
    );
  }

  if (!srcdocHtml && !srcUrl && !videoInfo) {
    return (
      <Box sx={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Preview unavailable</Typography>
      </Box>
    );
  }

  const currentW = isExpanded ? expandDims.width : w;
  const currentH = isExpanded ? expandDims.height : h;

  const hasScreenshot = !!preview.screenshot_url;

  const renderVideoContent = () => (
    <Box sx={{ width: w, height: h, bgcolor: 'black', overflow: 'hidden', flexShrink: 0 }}>
      <video
        src={videoInfo!.videoUrl}
        controls
        autoPlay
        muted
        loop
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
    </Box>
  );

  const renderScreenshotContent = () => (
    <Box sx={{ width: w, height: h, bgcolor: 'white', overflow: 'hidden', flexShrink: 0 }}>
      <Box
        component="img"
        src={preview.screenshot_url}
        alt={preview.name}
        crossOrigin="anonymous"
        sx={{ width: w, height: h, objectFit: 'contain', display: 'block' }}
      />
    </Box>
  );

  const renderIframeContent = () => (
    <Box sx={{ position: 'relative', width: isExpanded ? expandDims.width : w, height: isExpanded ? expandDims.height : h, bgcolor: 'white', overflow: 'hidden', flexShrink: 0, transition: 'all 0.2s ease', zIndex: isExpanded ? 100 : 'auto' }}>
      <iframe
        ref={iframeRef}
        srcDoc={srcdocHtml || undefined}
        src={srcUrl || undefined}
        title={preview.name}
        sandbox={`allow-scripts${!celtraTag ? ' allow-same-origin' : ''} allow-modals allow-popups allow-forms${!celtraTag ? ' allow-top-navigation' : ''} allow-popups-to-escape-sandbox`}
        scrolling="no"
        style={{ width: currentW, height: currentH, border: 'none', display: 'block' }}
      />
      {isExpanded && (
        <Box
          onClick={handleClose}
          sx={{
            position: 'absolute', top: 4, right: 4, zIndex: 110,
            width: 24, height: 24, borderRadius: '50%',
            bgcolor: 'rgba(0,0,0,0.6)', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', fontSize: 16, lineHeight: 1,
            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
          }}
        >
          &times;
        </Box>
      )}
    </Box>
  );

  const cardMaxWidth = 320;
  const frameDims = { mobile: 375, tablet: 820, desktop: 1440, ctv: 1920 };
  const frameW = frameDims[deviceType] || 375;
  const fitScale = Math.min(1, cardMaxWidth / frameW) * zoomScale;
  const frameH = { mobile: 667, tablet: 1180, desktop: 900, ctv: 1080 };
  const displayH = (frameH[deviceType] || 667) * fitScale;

  return (
    <Box sx={{ position: 'relative', '&:hover .device-type-toggle': { opacity: 1 } }}>
      <Checkbox
        checked={selected}
        onChange={() => onToggleSelect(preview.creative_id)}
        size="small"
        sx={{ position: 'absolute', top: 4, left: 4, zIndex: 10, bgcolor: 'rgba(255,255,255,0.8)', borderRadius: 1, p: 0.25 }}
      />
      <ToggleButtonGroup
        className="device-type-toggle"
        value={deviceType}
        exclusive
        onChange={(_, v) => v && onDeviceTypeChange(preview.creative_id, v)}
        size="small"
        sx={{
          position: 'absolute', top: 4, right: 4, zIndex: 10,
          bgcolor: 'rgba(255,255,255,0.9)', borderRadius: 1,
          opacity: 0, transition: 'opacity 0.2s',
          '& .MuiToggleButton-root': { p: 0.25, border: 'none', '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } } },
        }}
      >
        <ToggleButton value="mobile"><PhoneIphoneIcon sx={{ fontSize: 16 }} /></ToggleButton>
        <ToggleButton value="tablet"><TabletIcon sx={{ fontSize: 16 }} /></ToggleButton>
        <ToggleButton value="desktop"><DesktopWindowsIcon sx={{ fontSize: 16 }} /></ToggleButton>
        <ToggleButton value="ctv"><TvIcon sx={{ fontSize: 16 }} /></ToggleButton>
      </ToggleButtonGroup>
      <Box sx={{ height: displayH, overflow: 'hidden', display: 'flex', justifyContent: 'center' }}>
        <Box ref={cardRef} sx={{ transform: `scale(${fitScale})`, transformOrigin: 'top center' }}>
          <DeviceFrame
            deviceType={deviceType}
            adWidth={w}
            adHeight={h}
            orientation={orientation}
            chromeTheme={chromeTheme === 'dark' ? 'light' : 'dark'}
            showSizeLabel={showSizeLabel}
            showNameLabel={showNameLabel ? preview.name : undefined}
            siteContext={siteContext}
            autoScale={false}
            scale={1}
          >
            {videoInfo && renderVideoContent()}
            {!videoInfo && hasScreenshot && renderScreenshotContent()}
            {!videoInfo && !hasScreenshot && renderIframeContent()}
          </DeviceFrame>
        </Box>
      </Box>
    </Box>
  );
}
